import { format } from 'date-fns';

import type { NotificationItem } from '../interfaces/notificationItem';

import Constants from '../constants';

const DATE_FNS_FORMAT = 'h:mm a, MMM do';

const getExpirationDate = (expirationDate: string) => {
  const todaysDate = new Date() + '';

  return Math.floor((Date.parse(expirationDate) - Date.parse(todaysDate)) / 86400000);
};

const formatNotifications = (notification: NotificationItem) => {
  const dateInLocalTime = new Date(Number(notification.dateCreatedTimestamp));

  const selectNotificationText = {
    [Constants.NOTIFICATION_TYPES.LEAD_ADDED]: () => {
      const { street = '', leadContactName = '', unit = '', message } = notification.payload;
      if (message) {
        return message;
      }

      const unitText = unit.length > 0 ? ` unit ${unit}` : '';
      return `${leadContactName} is interested in ${street}${unitText}`;
    },
    [Constants.NOTIFICATION_TYPES.SYNDICATION_STATUS_UPDATED]: () => {
      const {
        brand,
        isSingleNotification = false,
        street,
        unit = '',
        userPropertySyndicationBrandDto,
        userPropertySyndicationOverviewStatus,
      }: NotificationItem['payload'] = notification.payload;
      const unitText = unit.length > 0 ? ` unit ${unit}` : '';
      const listingAddress = `${street}${unitText}`;
      const isPosted = userPropertySyndicationBrandDto?.displayStatus === 'Posted';

      let notificationText = isPosted
        ? // @ts-ignore
          `${listingAddress} is now live on ${Constants.FORMATTED_BRAND_NAMES[(brand || '').toLowerCase()]}!`
        : // @ts-ignore
          `${listingAddress} was declined on ${Constants.FORMATTED_BRAND_NAMES[(brand || '').toLowerCase()]}!`;

      if (isSingleNotification) {
        // @ts-ignore
        switch (userPropertySyndicationOverviewStatus.toUpperCase()) {
          case 'PUBLISHED':
            notificationText = `${listingAddress} is now published.`;
            break;

          case 'PARTIALLY_PUBLISHED':
            notificationText = `${listingAddress} is now partially published.`;
            break;

          case 'DECLINED':
            notificationText = `${listingAddress} was declined.`;
            break;

          default:
            break;
        }
      }

      return notificationText;
    },
    [Constants.NOTIFICATION_TYPES.LISTING_EXPIRED]: () => {
      const { street, unit = '' }: NotificationItem['payload'] = notification.payload;
      const unitText = unit.length > 0 ? `Unit ${unit} ` : '';

      return `${street} ${unitText}has expired.`;
    },
    [Constants.NOTIFICATION_TYPES.LISTING_NEARLY_EXPIRED]: () => {
      const { street, unit = '', expirationDate }: NotificationItem['payload'] = notification.payload;
      const unitText = unit.length > 0 ? `Unit ${unit} ` : '';
      const expirationDays = getExpirationDate(String(expirationDate));

      const expirationText =
        expirationDays < 1
          ? `${street} ${unitText}will expire today.`
          : `${street} ${unitText}will expire in ${expirationDays} days.`;

      return `${expirationText} Deactivate your listing if you have found a renter or extend it
            if you're still looking for one.`;
    },
    [Constants.NOTIFICATION_TYPES.CONVERSATION_UPDATED]: () => {
      const { title } = notification.payload;

      return title;
    },
    [Constants.NOTIFICATION_TYPES.APPLICATION_RECEIVED]: () => {
      const { message = '' } = notification.payload;

      return message;
    },
    [Constants.NOTIFICATION_TYPES.APPLICATION_UNREAD]: () => {
      const { message = '' } = notification.payload;

      return message;
    },
    [Constants.NOTIFICATION_TYPES.APPLICATION_WITHDRAWN]: () => {
      const { message = '' } = notification.payload;

      return message;
    },
    [Constants.NOTIFICATION_TYPES.CUSTOMER_LISTING_PROBLEM]: () => {
      const { message, street = '', unit = '' }: NotificationItem['payload'] = notification.payload;
      const streetText = street.length > 0 ? ` for ${street}` : '';
      const unitText = unit.length > 0 ? ` Unit ${unit}` : '';

      return `${message}${streetText}${unitText}`;
    },
    [Constants.NOTIFICATION_TYPES.NEW_UPDATED_INVOICE]: () => {
      const { months = [] } = notification.payload;
      const monthText = months.length == 1 ? ` ${months[0]}` : '';

      return `A new${monthText} invoice is now available`;
    },
    [Constants.NOTIFICATION_TYPES.USER_MANAGEMENT_INVITATION]: () => {
      // @ts-ignore
      const { userManagementCompanyName = '' } = notification.payload;

      return `You have been invited to join ${userManagementCompanyName}`;
    },
    [Constants.NOTIFICATION_TYPES.REVIEW_CREATED]: () => {
      const { listingName = '' } = notification.payload;
      return `${listingName} has a new review!`;
    },
    [Constants.NOTIFICATION_TYPES.DRAFT_REMINDER]: () => {
      const { street = '', unit = '' } = notification.payload;

      const streetText = street.length > 0 ? `${street}` : '';
      const unitText = unit.length > 0 ? ` Unit ${unit}` : '';

      return `${streetText}${unitText} has an open draft.`;
    },
    [Constants.NOTIFICATION_TYPES.ENTERPRISE_LISTING_DECLINED]: () => {
      const { street = '', unit = '' } = notification.payload;
      const unitText = unit.length > 0 ? ` Unit ${unit}` : '';

      return `${street}${unitText} was declined.`;
    },
  };
  const getNotificationText = selectNotificationText[notification.type] || null;
  const notificationText = getNotificationText ? getNotificationText() : 'You have a new Notification';

  // TODO: remove typo variable
  return {
    notfificationText: notificationText,
    notificationText,
    timeStamp: format(new Date(dateInLocalTime), DATE_FNS_FORMAT),
    ...notification,
  };
};

export default formatNotifications;
